import { Fragment } from 'react';

import CaretDown from '@decisiv/iconix/lib/components/CaretDown';
import EllipsisH from '@decisiv/iconix/lib/components/EllipsisH';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Menu from '@decisiv/ui-components/lib/components/Menu';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import useMenuPopover from 'pages/Management/hooks/useMenuPopover';
import type { MenuButtonItem } from 'pages/Management/components/MenuButton';
import { MenuDivider } from 'pages/Management/Account/Depots/AddAssetModal/styles';

export interface MenuSection {
  id: string;
  /** Upper-case group heading, e.g. "ASSETS" in the Figma row menus. */
  title?: string;
  items: MenuButtonItem[];
  /** Explanatory copy under the last item, e.g. for Replace Asset List. */
  helpText?: string;
}

type SectionedMenuButtonProps = {
  sections: MenuSection[];
  disabled?: boolean;
  kind?: 'primary' | 'secondary';
  zIndex?: number;
} & (
  | { text: string; 'aria-label'?: string }
  | { text?: never; 'aria-label': string }
);

/**
 * A MenuButton whose popover stacks several KDS Menus with headings and help
 * text, as in the Team page Figma menus (KDS Menu has neither). With no
 * `text` it renders the "⋯" row actions trigger.
 */
export default function SectionedMenuButton({
  sections,
  text,
  disabled,
  kind = 'secondary',
  zIndex = 200,
  'aria-label': ariaLabel,
}: SectionedMenuButtonProps) {
  const { triggerProps, popoverProps, closeOnSelect, menuRef, menuStyle } =
    useMenuPopover({ keepTriggerVisible: true, align: 'end' });
  const visibleSections = sections.filter(({ items }) => items.length > 0);

  return (
    <>
      <Button
        {...triggerProps}
        size="small"
        kind={kind}
        text={text}
        icon={text ? CaretDown : EllipsisH}
        iconPosition={text ? 'right' : undefined}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <Popover {...popoverProps} zIndex={zIndex}>
        <Flex
          ref={menuRef}
          flexDirection="column"
          style={{ minWidth: 280, maxWidth: 280, ...menuStyle }}
        >
          {visibleSections.map((section, index) => (
            <Fragment key={section.id}>
              {index > 0 && <MenuDivider />}
              {section.title && (
                <P
                  size="small"
                  weight="semibold"
                  paddingX={1}
                  paddingTop={0.5}
                  style={{ textTransform: 'uppercase' }}
                >
                  {section.title}
                </P>
              )}
              <Menu items={closeOnSelect(section.items)} />
              {section.helpText && (
                <P size="small" shade={1} paddingX={1} paddingBottom={0.5}>
                  {section.helpText}
                </P>
              )}
            </Fragment>
          ))}
        </Flex>
      </Popover>
    </>
  );
}
